import { setup, assign, fromPromise } from "xstate"
import type { JellyfinClient } from "./JellyfinClient.ts"
import type { ProgressReporter } from "./ProgressReporter.ts"
import type { ActiveSession, ProjektorLoadData } from "./types.ts"

// ────────────────────────────────────────────────────────────────────────────
// Shape
//
// Two orthogonal concerns, modelled as parallel regions:
//   lifecycle: which session do we have loaded, and how far through the pre-play
//              pipeline are we?
//   playback:  what is the player actually doing right now — playing or paused?
//
// Splitting them removes the preplayPlaybackState hack: a PAUSE event during
// STARTING_PROGRESS now cleanly lands in playback=PAUSED while lifecycle keeps
// marching forward.  Session-attribution (stale-event filtering) happens at
// main.ts dispatch time, so state-scoped ERROR/MEDIA_FINISHED handlers here
// don't have to defend against it.
// ────────────────────────────────────────────────────────────────────────────

export interface PlaybackContext {
  /** Monotonic counter incremented on every LOAD. Used by main.ts to drop
   * CAF events attributed to a prior session (see cafEventIsStale). */
  epoch: number
  activeSession: ActiveSession | null
  progressReporter: ProgressReporter | null
  lastError: string | null
  lastErrorCode: string | null
  lastTransition: string
  lastTransitionTime: number
}

export type PlaybackEvent =
  | { type: "LOAD"; data: ProjektorLoadData; client: JellyfinClient; progressReporter: ProgressReporter; playSessionId: string; epoch: number }
  | { type: "RESET" }
  | { type: "PLAYING" }
  | { type: "PAUSE" }
  | { type: "SEEKED" }
  | { type: "MEDIA_FINISHED"; finalPositionTicks: number }
  | { type: "STOP" }
  | { type: "CANCEL" }
  | { type: "TIME_UPDATE"; currentTime?: number }
  | { type: "QUEUE_CHANGED" }
  | { type: "ERROR"; errorCode?: string; errorDescription?: string; error?: string }
  | { type: "MEDIA_ERROR"; errorCode?: string; errorDescription?: string; error?: string }
  | { type: "LOAD_CANCELLED" }

// ────────────────────────────────────────────────────────────────────────────
// Promise actors
// ────────────────────────────────────────────────────────────────────────────

const startProgressReporter = fromPromise(
  async ({ input }: { input: ProgressReporter }) => {
    await input.start()
  }
)

const stopProgressReporter = fromPromise(
  async ({ input }: { input: { reporter: ProgressReporter; finalTicks: number } }) => {
    await input.reporter.stop(input.finalTicks)
  }
)

// ────────────────────────────────────────────────────────────────────────────
// Machine
// ────────────────────────────────────────────────────────────────────────────

export const playbackMachine = setup({
  types: {
    context: {} as PlaybackContext,
    events: {} as PlaybackEvent,
  },
  actors: {
    startProgressReporter,
    stopProgressReporter,
  },
  actions: {
    assignSession: assign(({ event }) => {
      if (event.type !== "LOAD") return {}
      return {
        epoch: event.epoch,
        activeSession: { data: event.data, playSessionId: event.playSessionId },
        progressReporter: event.progressReporter,
        lastError: null,
        lastTransition: "LOAD",
        lastTransitionTime: Date.now(),
      }
    }),
    clearSession: assign({
      activeSession: null,
      progressReporter: null,
      lastTransition: "CLEARED",
      lastTransitionTime: Date.now(),
      lastError: null,
      lastErrorCode: null,
    }),
    // Fire-and-forget teardown of the *previous* session's side-effecting
    // subsystems when a new LOAD replaces it (e.g. queue autoplay).
    // stop() with no arg falls back to lastReportedTicks (up to ~10s stale)
    // — the player has already been reset() by CAF before the LOAD
    // interceptor runs, so we cannot capture an accurate position here.
    teardownPriorSession: ({ context }) => {
      context.progressReporter?.stop().catch(() => {})
    },
    recordError: assign(({ event }) => {
      const error = "error" in event ? String(event.error) : null
      const errorCode = "errorCode" in event ? String(event.errorCode) : null
      return {
        lastError: error,
        lastErrorCode: errorCode,
        lastTransition: "ERROR",
        lastTransitionTime: Date.now(),
      }
    }),
    recordTransition: assign(({ event }) => ({
      lastTransition: event.type,
      lastTransitionTime: Date.now(),
    })),
  },
}).createMachine({
  id: "playback",
  type: "parallel",
  context: {
    epoch: 0,
    activeSession: null,
    progressReporter: null,
    lastError: null,
    lastErrorCode: null,
    lastTransition: "INIT",
    lastTransitionTime: Date.now(),
  },
  states: {
    lifecycle: {
      initial: "IDLE",
      states: {
        IDLE: {
          on: {
            LOAD: {
              target: "SESSION_READY",
              actions: ["teardownPriorSession", "assignSession"],
            },
            RESET: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
          },
        },

        SESSION_READY: {
          on: {
            // CAF fires PLAYING normally, or SEEKED for queue autoplay.
            PLAYING: { target: "STARTING_PROGRESS", actions: ["recordTransition"] },
            SEEKED: { target: "STARTING_PROGRESS", actions: ["recordTransition"] },
            LOAD: {
              target: "SESSION_READY",
              actions: ["teardownPriorSession", "assignSession"],
            },
            RESET: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
            // No MEDIA_FINISHED / ERROR handler: stale events for a prior
            // session are filtered by main.ts session-attribution guard, so
            // anything arriving here genuinely would not apply.
          },
        },

        STARTING_PROGRESS: {
          invoke: {
            src: "startProgressReporter",
            input: ({ context }) => context.progressReporter!,
            onDone: { target: "READY", actions: ["recordTransition"] },
            onError: { target: "ERROR", actions: ["recordError"] },
          },
          on: {
            LOAD: {
              target: "SESSION_READY",
              actions: ["teardownPriorSession", "assignSession"],
            },
            RESET: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
          },
        },

        READY: {
          on: {
            MEDIA_FINISHED: { target: "STOPPING_PROGRESS", actions: ["recordTransition"] },
            LOAD: {
              target: "SESSION_READY",
              actions: ["teardownPriorSession", "assignSession"],
            },
            ERROR: { target: "ERROR", actions: ["recordError"] },
            MEDIA_ERROR: { target: "ERROR", actions: ["recordError"] },
            RESET: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
          },
        },

        STOPPING_PROGRESS: {
          invoke: {
            src: "stopProgressReporter",
            input: ({ context, event }) => ({
              reporter: context.progressReporter!,
              finalTicks: event.type === "MEDIA_FINISHED" ? event.finalPositionTicks : 0,
            }),
            onDone: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
            onError: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
          },
          on: {
            // CAF queue auto-advance can issue LOAD while we're still stopping
            // the prior session. Accept it and transition to the new session.
            LOAD: {
              target: "SESSION_READY",
              actions: ["teardownPriorSession", "assignSession"],
            },
            RESET: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
          },
        },

        ERROR: {
          on: {
            LOAD: {
              target: "SESSION_READY",
              actions: ["teardownPriorSession", "clearSession", "assignSession"],
            },
            RESET: { target: "IDLE", actions: ["clearSession", "recordTransition"] },
          },
        },
      },
    },

    playback: {
      initial: "NONE",
      states: {
        NONE: {
          on: {
            PLAYING: { target: "PLAYING" },
            PAUSE: { target: "PAUSED" },
            // LOAD/RESET/MEDIA_FINISHED/ERROR reset playback to NONE — they
            // all mean "the current session's playback is no longer relevant".
            LOAD: { target: "NONE" },
            RESET: { target: "NONE" },
            MEDIA_FINISHED: { target: "NONE" },
            ERROR: { target: "NONE" },
            MEDIA_ERROR: { target: "NONE" },
          },
        },
        PLAYING: {
          on: {
            PAUSE: { target: "PAUSED" },
            PLAYING: { target: "PLAYING" },
            LOAD: { target: "NONE" },
            RESET: { target: "NONE" },
            MEDIA_FINISHED: { target: "NONE" },
            ERROR: { target: "NONE" },
            MEDIA_ERROR: { target: "NONE" },
          },
        },
        PAUSED: {
          on: {
            PLAYING: { target: "PLAYING" },
            PAUSE: { target: "PAUSED" },
            LOAD: { target: "NONE" },
            RESET: { target: "NONE" },
            MEDIA_FINISHED: { target: "NONE" },
            ERROR: { target: "NONE" },
            MEDIA_ERROR: { target: "NONE" },
          },
        },
      },
    },
  },
})
